import { Fragment, ReactElement, ReactNode, useState } from 'react';
import { formatNumber } from './breakdownFormat';

export interface CrrRow {
  label: string;
  description: string;
  // value column entries — `power` is shown in the W column when finite, otherwise the row
  // can supply a custom `valueLabel` to display in place (used by ratio rows which are
  // unitless rather than powers).
  power?: number;
  work?: number;
  valueLabel?: string;
  // Pass `null` to suppress the expand toggle on this row (used for pipeline-value ratio
  // rows that don't have a derivation worth showing).
  expandedContent: ReactNode | null;
}

interface Props {
  duration: number;
  mainRows: CrrRow[];      // Alpha, Aero, Rider, Kinetic, Gravitational, Rolling Power
  ratioRows: CrrRow[];     // Crr Raw, Crr @ 20°C — unitless rows after the divider
  crrAt20C: number;        // shown in the rightmost rowSpan column
}

function ToggleButton({
  isExpanded,
  onToggle,
}: {
  isExpanded: boolean;
  onToggle: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={isExpanded ? 'Collapse' : 'Expand'}
      className={
        'cr-w-6 cr-h-6 cr-flex cr-items-center cr-justify-center cr-rounded ' +
        'cr-border cr-border-gray-300 cr-bg-white cr-text-gray-700 cr-text-sm cr-font-bold ' +
        'hover:cr-bg-blue-50 hover:cr-border-blue-500 hover:cr-text-blue-700'
      }
    >
      {isExpanded ? '−' : '+'}
    </button>
  );
}

export default function RawCrrTable({
  duration,
  mainRows,
  ratioRows,
  crrAt20C,
}: Props): ReactElement {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (label: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  return (
    <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md cr-mb-6">
      <h3 className="font-montserrat cr-text-lg cr-font-semibold cr-text-blue-900 cr-mb-3">
        Raw Crr
      </h3>
      <div className="cr-flex cr-flex-wrap cr-gap-x-6 cr-gap-y-1 cr-text-sm cr-text-gray-600 cr-mb-3">
        <span>
          <span className="cr-font-medium cr-text-gray-800">Time:</span>{' '}
          {formatNumber(duration, 2)} s
        </span>
      </div>
      <table className="cr-w-full cr-text-sm">
        <thead>
          <tr className="cr-border-b cr-border-gray-300 cr-text-gray-700">
            <th className="cr-w-8"></th>
            <th className="cr-text-left cr-py-2 cr-pr-4">Component</th>
            <th className="cr-text-left cr-py-2 cr-pr-4">Description</th>
            <th className="cr-text-right cr-py-2 cr-pr-4">Work (J) / Value</th>
            <th className="cr-text-right cr-py-2 cr-pr-4">Power (W)</th>
            <th className="cr-py-2"></th>
          </tr>
        </thead>
        <tbody>
          {mainRows.map((row) => {
            const isExpanded = expanded.has(row.label);
            return (
              <Fragment key={row.label}>
                <tr className="cr-border-b cr-border-gray-100">
                  <td className="cr-py-2 cr-text-center">
                    <ToggleButton isExpanded={isExpanded} onToggle={() => toggle(row.label)} />
                  </td>
                  <td className="cr-py-2 cr-pr-4 cr-font-medium">{row.label}</td>
                  <td className="cr-py-2 cr-pr-4 cr-text-gray-500">{row.description}</td>
                  <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono">
                    {row.valueLabel
                      ?? (Number.isFinite(row.work as number) ? formatNumber(row.work as number, 0) : '—')}
                  </td>
                  <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono">
                    {Number.isFinite(row.power as number) ? formatNumber(row.power as number, 1) : '—'}
                  </td>
                  <td className="cr-py-2"></td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={6} className="cr-bg-gray-50 cr-px-4 cr-py-3 cr-border-b cr-border-gray-100">
                      {row.expandedContent}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          <tr>
            <td colSpan={6} className="cr-py-0">
              <div className="cr-border-t-2 cr-border-blue-900 cr-mt-1" />
            </td>
          </tr>
          {/*
            Render every ratio row consecutively (no expansion <tr>s in between) so the
            rowSpan'd Crr@20°C cell from the first row spans cleanly down through them.
            Expansion <tr>s are appended below the block, each with full colSpan.
          */}
          {ratioRows.map((row, idx) => {
            const isExpanded = expanded.has(row.label);
            const isFirst = idx === 0;
            const expandable = row.expandedContent != null;
            return (
              <tr key={row.label} className="cr-bg-blue-50 cr-border-b cr-border-gray-200">
                <td className="cr-py-2 cr-text-center">
                  {expandable && (
                    <ToggleButton isExpanded={isExpanded} onToggle={() => toggle(row.label)} />
                  )}
                </td>
                <td className="cr-py-2 cr-pr-4 cr-font-semibold cr-text-blue-900">{row.label}</td>
                <td className="cr-py-2 cr-pr-4 cr-text-gray-600 cr-italic">{row.description}</td>
                <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono cr-font-semibold">
                  {row.valueLabel ?? '—'}
                </td>
                <td className="cr-py-2 cr-pr-4 cr-text-right cr-font-mono">—</td>
                {isFirst && (
                  <td
                    rowSpan={ratioRows.length}
                    className="cr-py-2 cr-pl-4 cr-border-l cr-border-gray-300 cr-align-middle cr-text-center"
                  >
                    <div className="cr-text-xs cr-text-gray-500 cr-uppercase cr-tracking-wide">
                      Crr @ 20°C
                    </div>
                    <div className="cr-text-2xl cr-font-bold cr-text-blue-900 cr-font-mono">
                      {formatNumber(crrAt20C, 5)}
                    </div>
                    <div className="cr-text-xs cr-text-gray-500 cr-mt-1 cr-italic">
                      temperature-normalised
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
          {ratioRows
            .filter((r) => r.expandedContent != null && expanded.has(r.label))
            .map((row) => (
              <tr key={`expansion-${row.label}`}>
                <td
                  colSpan={6}
                  className="cr-bg-gray-50 cr-px-4 cr-py-3 cr-border-b cr-border-gray-100"
                >
                  <div className="cr-text-xs cr-text-gray-500 cr-uppercase cr-tracking-wide cr-mb-2">
                    {row.label}
                  </div>
                  {row.expandedContent}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      <p className="cr-text-xs cr-text-gray-500 cr-mt-3 cr-italic">
        Power = Work ÷ Time. Above the divider: energy components (Aero predicted from adjusted CdA;
        Rolling Power is the magnitude needed to close the energy balance). Below: Crr derivation
        from rolling power.
      </p>
    </div>
  );
}
